/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {Component, Vue} from 'vue-property-decorator';
import {Getter} from 'vuex-class';
import {CommunityChallengeInterface} from '@/store/modules/community-challenge/types';
import {RewardAchievementSet, RewardType, TrophyGrade} from '@/store/modules/trophy-cabinet/types';
import Popper from 'vue-popperjs';
import moment from 'moment';
import {SpeciesInterface} from '@/store/modules/game/types';

/**
 * Community challenge Listing Component.
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-community-challenge-listing',
    components: {
        popper: Popper,
    },
})
export default class CommunityChallengeListing extends Vue {
    @Getter('getSpeciesById', {namespace: 'gameModule'})
    public getSpeciesById!: (id: string) => SpeciesInterface;

    @Getter('challenges', {namespace: 'communityChallengesModule'})
    public challenges!: CommunityChallengeInterface[];

    /**
     * Determines is a community challenge is active.
     * @param {CommunityChallengeInterface} challenge
     * @return {boolean}
     */
    public isActive(challenge: CommunityChallengeInterface): boolean {
        return !challenge.expired && challenge.commenced;
    }

    /**
     * Time in ISO format.
     * @param {number} timestamp
     * @return {string}
     */
    public formatTime(timestamp: number): string {
        return moment.unix(timestamp).format('Y-M-D');
    }

    /**
     * Determine the remaining time.
     * @return {string}
     */
    public remaining(challenge: CommunityChallengeInterface): string {
        let expiry: number = challenge.expiresAtTimestamp - challenge.serverTimestamp;

        if (expiry < 0) {
            expiry = 0;
        }

        const days: number = Math.floor(expiry / (3600 * 24));
        if (days > 0) {
            return this.$i18n.translate('time.left.days', '', {days}, days) || '';
        }

        const hours: number = Math.floor(expiry / 3600);
        if (hours > 0) {
            return this.$i18n.translate('time.left.hours', '', {hours}, hours) || '';
        }

        const minutes: number = Math.floor((expiry - (hours * 3600)) / 60);
        return this.$i18n.translate('time.left.minutes', '', {minutes}, minutes) || '';
    }

    /**
     * @param {CommunityChallengeInterface} challenge
     * @return {string}
     */
    public buildTrophyImage(challenge: CommunityChallengeInterface): any {
        try {
            const uri: string = challenge.trophy || TrophyGrade.NONE;
            return require(`@/assets/icons/award_${uri}.svg`);
        } catch (e) {
            // do nothing here
        }
    }

    /**
     * @param {RewardType} type
     * @return {any}
     */
    public buildRewardImage(type: RewardType): any {
        try {
            switch (type) {
                case RewardType.RewardAnimal: return require('@/assets/icons/animal.svg');
                case RewardType.RewardCoin: return require('@/assets/icons/price_cash.svg');
                case RewardType.RewardCredit: return require('@/assets/icons/price_credits.svg');
                case RewardType.RewardAvatarGlasses: return require('@/assets/icons/cosmetic_face.svg');
                case RewardType.RewardAvatarHat: return require('@/assets/icons/cosmetic_hat.svg');
                case RewardType.RewardAvatarShirt: return require('@/assets/icons/cosmetic_top.svg');
                case RewardType.RewardAvatarShorts: return require('@/assets/icons/cosmetic_pants.svg');
            }
        } catch (e) {
            // do nothing here
        }
    }

    /**
     * Build classes for community challenges.
     * @param {RewardAchievementSet} reward
     * @param {CommunityChallengeInterface} challenge
     * @return {string[]}
     */
    public buildRewardClasses(reward: RewardAchievementSet, challenge: CommunityChallengeInterface): string[] {
        const classes: string[] = [];

        if (!!reward.personal) {
            classes.push('complete');
        }

        if (!!reward.completion) {
            classes.push(`award-${challenge.trophy || TrophyGrade.NONE}`);
        }

        return classes;
    }

    /**
     * Determine if the user has contributed to challenge.
     * @param {CommunityChallengeInterface} challenge
     * @return {boolean}
     */
    public hasContributed(challenge: CommunityChallengeInterface): boolean {
        return !!challenge.contribution;
    }

    /**
     * Get the translated personal contribution text.
     * @param {CommunityChallengeInterface} challenge
     * @return {string}
     */
    public personalContributionText(challenge: CommunityChallengeInterface): string {
        if (!challenge.target) {
            return '';
        }

        const species: SpeciesInterface = this.getSpeciesById(`/api/zoo/species/${challenge.target}`);
        if (!species) {
            return '';
        }
        return this.$i18n.translate(
            `achievements.cc.${challenge.event}`,
            '',
            {count: challenge.contribution, species: species.commonName},
        ) || `${challenge.contribution}`;
    }

    /**
     * Get the filtered challenge title
     * @param {CommunityChallengeInterface} challenge
     * @return {string}
     */
    public title(challenge: CommunityChallengeInterface): string {
        return (challenge.title || '').replace('{count}', `${challenge.quantity}`);
    }

    /**
     * Get the filtered challenge synopsis
     * @param {CommunityChallengeInterface} challenge
     * @return {string}
     */
    public synopsis(challenge: CommunityChallengeInterface): string {
        return (challenge.synopsis || '').replace('{count}', `${challenge.quantity}`);
    }

    /**
     * Get the filtered challenge description
     * @param {CommunityChallengeInterface} challenge
     * @return {string}
     */
    public description(challenge: CommunityChallengeInterface): string {
        return (challenge.description || '').replace('{count}', `${challenge.quantity}`);
    }
}
